import { baseUrl, routes } from "@/seo/routes";
import { DEFAULT_LANG, LANGUAGES, XDEFAULT_LANG } from "@/i18n/languages";

/**
 * La langue d'une route, et ses équivalents dans les autres langues.
 *
 * Le site est bâti sur une carte de routes à plat, chemin par chemin : les
 * pages françaises vivent sous /fr, les anglaises sous /en, les allemandes
 * sous /de. Plutôt que de dupliquer cette carte par langue, chaque route porte
 * deux champs optionnels — `lang`, qui vaut "fr" par défaut, et `group`, le
 * sujet que la page traite.
 *
 * Le groupe, et non un lien vers la traduction : une paire ne survit pas à une
 * troisième langue, puisqu'il faudrait alors retoucher chaque route existante
 * pour qu'elle cite la nouvelle. Avec un groupe, ajouter une langue n'ajoute
 * que ses propres routes.
 *
 * Un groupe d'une seule route n'émet aucune balise hreflang : annoncer une
 * traduction qui n'existe pas envoie le visiteur sur une 404, ce que Google
 * signale comme une erreur de ciblage linguistique.
 */

// Derivees du registre : une langue servie ne peut pas manquer a l'appel.
const byLang = (field) =>
    Object.fromEntries(Object.entries(LANGUAGES).map(([lang, data]) => [lang, data[field]]));

/** L'attribut lang du <html>, par langue. */
export const HTML_LANG = byLang("html");

/** Open Graph veut une locale complète, pas un code de langue. */
export const OG_LOCALE = byLang("ogLocale");

export { DEFAULT_LANG, XDEFAULT_LANG };

const abs = (path) => baseUrl + (path === "/" ? "/" : path);

/** La langue de la route, ou le français si elle n'en déclare pas. */
export function langOf(path) {
    return routes[path]?.lang ?? DEFAULT_LANG;
}

/**
 * Les chemins d'un groupe de traduction, dans l'ordre de seoConfig.
 *
 * Les pages en noindex en sont exclues : une traduction redigee d'avance,
 * publiee mais volontairement tenue hors index, ne doit pas etre annoncee en
 * hreflang — ce serait demander a Google de suivre un lien vers une page qu'on
 * lui demande par ailleurs d'ignorer. Retirer le noindex suffit donc a faire
 * apparaitre les balises, sans autre geste.
 */
export function groupPaths(group) {
    if (!group) return [];
    return Object.keys(routes).filter(
        (path) => routes[path].group === group && !routes[path].noindex
    );
}

/** Tous les chemins d'un groupe, brouillons compris, pour les contrôles. */
export function groupPathsIncludingDrafts(group) {
    if (!group) return [];
    return Object.keys(routes).filter((path) => routes[path].group === group);
}

/**
 * Le chemin du groupe dans la langue demandée, ou null.
 *
 * Brouillons compris, contrairement aux hreflang : `noindex` dit à Google de ne
 * pas indexer, pas à l'utilisateur de ne pas y aller. Les exclure faisait qu'une
 * page anglaise renvoyait vers l'accueil et les règles françaises, et que son
 * fil d'Ariane ne pouvait jamais trouver son parent anglais.
 */
export function pathInLanguage(group, lang) {
    return groupPathsIncludingDrafts(group).find((path) => langOf(path) === lang) || null;
}

/**
 * Les balises hreflang d'une route : une par langue du groupe, plus x-default.
 * Tableau vide tant que le groupe ne compte qu'une seule page.
 */
export function alternatesOf(path) {
    const meta = routes[path];
    const paths = groupPaths(meta?.group);
    if (paths.length < 2) return [];

    const tags = paths.map((p) => ({ hreflang: langOf(p), href: abs(p) }));
    const fallback = tags.find((tag) => tag.hreflang === XDEFAULT_LANG);
    if (fallback) tags.push({ hreflang: "x-default", href: fallback.href });

    return tags;
}

/** Les groupes de traduction et leurs routes, pour les contrôles du build. */
export function translationGroups() {
    const groups = {};
    for (const [path, meta] of Object.entries(routes)) {
        if (!meta.group) continue;
        (groups[meta.group] = groups[meta.group] || []).push(path);
    }
    return groups;
}
